import { useEffect } from "react";
import { UseFormReturn } from "react-hook-form";
import { CHLD, CHLD_TEMPLATE } from "@/models/chld";
import { validateEffectiveStartDateForProgramYear } from "@/utils/dates";

export function convertEthnicityFromBackend(
  value: string | boolean | undefined | null,
  missingIndicator: string | boolean | undefined | null
): string | undefined {
  if (value === true || value === "Y") return "Y";
  if (value === false || value === "N") return "N";
  // Only return "" (No Response) if the missing indicator confirms it was explicitly selected
  if (missingIndicator === "Y" || missingIndicator === true) return "";
  return undefined; // No selection yet
}

export function buildChildAddDefaults(record: CHLD): CHLD {
  const ethnicityValue = convertEthnicityFromBackend(
    record.ChildEthnicity,
    record.ChildEthnicityMissingIndicator
  );
  const ethnicityMissingIndicator =
    ethnicityValue === "Y" || ethnicityValue === "N" ? "N" : "Y";

  const races = [
    record.ChildRace1,
    record.ChildRace2,
    record.ChildRace3,
    record.ChildRace4,
    record.ChildRace5,
  ].filter(Boolean);
  const raceMissingIndicator = races.length > 0 ? "N" : "Y";

  return {
    ...CHLD_TEMPLATE,
    ReportingLEA: record.ReportingLEA,
    CSPPID: record.CSPPID,
    ProgramYear: record.ProgramYear,
    PreschoolCode: record.PreschoolCode,
    LocalChildId: record.LocalChildId,
    SSID: record.SSID,
    ChildFirstName: record.ChildFirstName,
    ChildMiddleName: record.ChildMiddleName,
    ChildLastName: record.ChildLastName,
    ChildNameSuffix: record.ChildNameSuffix,
    ChildCountryOfBirth: record.ChildCountryOfBirth,
    ChildStateOfBirth: record.ChildStateOfBirth,
    ChildCityOfBirth: record.ChildCityOfBirth,
    ChildDateOfBirth: record.ChildDateOfBirth,
    ChildGender: record.ChildGender,
    ChildRace1: record.ChildRace1,
    ChildRace2: record.ChildRace2,
    ChildRace3: record.ChildRace3,
    ChildRace4: record.ChildRace4,
    ChildRace5: record.ChildRace5,
    IEPIFSPIndicator: record.IEPIFSPIndicator,
    ChildEthnicity: ethnicityValue ?? "",
    ChildEthnicityMissingIndicator: ethnicityMissingIndicator,
    ChildRaceMissingIndicator: raceMissingIndicator,
    Id: "",
    EffectiveStartDate: "",
    EffectiveEndDate: "",
  };
}

export function useCHLDFormLogic(
  form: UseFormReturn<CHLD, any, CHLD>,
  selectedChild: CHLD | undefined
) {
  const { setValue, watch, getValues, trigger } = form;

  // Reset form when selectedChild changes
  useEffect(() => {
    if (!selectedChild) return;

    const ethnicityValue = convertEthnicityFromBackend(
      selectedChild.ChildEthnicity,
      selectedChild.ChildEthnicityMissingIndicator
    );
    const ethnicityMissingIndicator =
      ethnicityValue === "Y" || ethnicityValue === "N" ? "N" : "Y";

    const races = [
      selectedChild.ChildRace1,
      selectedChild.ChildRace2,
      selectedChild.ChildRace3,
      selectedChild.ChildRace4,
      selectedChild.ChildRace5,
    ].filter(Boolean);
    const raceMissingIndicator = races.length > 0 ? "N" : "Y";

    form.reset({
      ...selectedChild,
      ChildEthnicity: ethnicityValue,
      ChildEthnicityMissingIndicator: ethnicityMissingIndicator,
      ChildRaceMissingIndicator: raceMissingIndicator,
    });
  }, [selectedChild?.CSPPID, form]);

  // Watch ethnicity and race fields, update missing indicators
  useEffect(() => {
    const subscription = watch((value, { name }) => {
      if (!name) return;

      // Ethnicity changed -> update missing indicator
      if (name === "ChildEthnicity") {
        const ethnicityValue = value.ChildEthnicity;
        const missingIndicator =
          ethnicityValue === "Y" || ethnicityValue === "N" ? "N" : "Y";
        setValue("ChildEthnicityMissingIndicator", missingIndicator, {
          shouldDirty: true,
        });
      }

      // Race fields changed -> update missing indicator
      if (
        name === "ChildRace1" ||
        name === "ChildRace2" ||
        name === "ChildRace3" ||
        name === "ChildRace4" ||
        name === "ChildRace5"
      ) {
        const races = [
          value.ChildRace1,
          value.ChildRace2,
          value.ChildRace3,
          value.ChildRace4,
          value.ChildRace5,
        ].filter(Boolean);
        const missingIndicator = races.length > 0 ? "N" : "Y";
        setValue("ChildRaceMissingIndicator", missingIndicator, {
          shouldDirty: true,
        });
      }
    });
    return () => subscription.unsubscribe();
  }, [watch, setValue]);

  // Initialize missing indicators on mount
  useEffect(() => {
    const currentEthnicity = getValues("ChildEthnicity");
    const currentEthnicityMissing = getValues("ChildEthnicityMissingIndicator");

    if (
      !currentEthnicityMissing &&
      currentEthnicity !== "Y" &&
      currentEthnicity !== "N"
    ) {
      setValue("ChildEthnicityMissingIndicator", "Y", { shouldDirty: false });
    }

    const races = getRaces();
    const currentRaceMissing = getValues("ChildRaceMissingIndicator");

    if (!currentRaceMissing && races.length === 0) {
      setValue("ChildRaceMissingIndicator", "Y", { shouldDirty: false });
    }
  }, []);

  // Cross-field validation triggers
  useEffect(() => {
    const subscription = watch((value, { name }) => {
      if (!name) return;

      // Program Year changed -> re-validate Effective Start Date
      if (name === "ProgramYear" && value.EffectiveStartDate) {
        trigger("EffectiveStartDate");
      }
    });
    return () => subscription.unsubscribe();
  }, [watch, trigger]);

  const getRaces = (): string[] => {
    const values = getValues();
    return [
      values.ChildRace1,
      values.ChildRace2,
      values.ChildRace3,
      values.ChildRace4,
      values.ChildRace5,
    ].filter(Boolean);
  };

  const setRaces = (values: string[]) => {
    setValue("ChildRace1", values[0] || "", { shouldDirty: true });
    setValue("ChildRace2", values[1] || "", { shouldDirty: true });
    setValue("ChildRace3", values[2] || "", { shouldDirty: true });
    setValue("ChildRace4", values[3] || "", { shouldDirty: true });
    setValue("ChildRace5", values[4] || "", { shouldDirty: true });
  };

  const validateEffectiveStartDate = (
    value: string | undefined
  ): string | true => {
    return validateEffectiveStartDateForProgramYear(
      value,
      getValues("ProgramYear")
    );
  };

  return {
    getRaces,
    setRaces,
    validateEffectiveStartDate,
    trigger,
  };
}
